import React, { useCallback, useEffect, useRef, useState } from 'react';

import { type CardDispute, CardDisputeCloseResolution } from '@column/api';
import { Button, formatNumber } from '@column/column-ui-kit';
import { Alert, AlertDescription, AlertTitle, Input, Label, Textarea } from '@column/ui';

import { DisputeEvidenceComposer } from '../Evidence/DisputeEvidenceComposer';
import {
  apiErrorMessage,
  useCloseCardDispute,
  useRespondCardDispute,
  useSubmitCardDispute,
} from '~/lib/api/cardDisputes';
import { formatRespondBy, getSubmissionStatusLabel } from '~/lib/disputes/labels';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import {
  type DisputeCloseResolutionDescriptor,
  type DisputeResponseActionDescriptor,
  type RespondFormInput,
  acceptedLiabilitySmallestUnit,
  buildRespondRequest,
  canRetrySubmissionIntent,
  getCloseResolutionDescriptors,
  getCloseResolutionLabel,
  getNextActor,
  getResponseActionDescriptors,
  getSubmissionIntent,
  isClosurePending,
  latestInboundEvidence,
  submissionIntentLabel,
  validateRespondFormInput,
} from './decisionMatrix';

const SUPPORT_PATH_COPY = 'Column could not determine the failed request safely — contact support to retry it.';

const emptyRespondInput: RespondFormInput = { narrative: '', amountInput: '', evidence: [] };

/**
 * The generated client throws the parsed API-error body (`{type, code, message, details}` — the
 * HTTP status is deliberately excluded from that JSON), so a 400/409 rejection arrives as a plain
 * object. Only a fetch that never received a response throws a `TypeError`, and that is the sole
 * case where the outcome is genuinely unknown.
 */
const isTransportFailure = (error: unknown): boolean => error instanceof TypeError;

/**
 * Part 05 — the dispute Actions area: stage-specific responses, failed-submission recovery, and
 * closure with explicit financial confirmations. Rendering is driven by the server's action
 * allowlists; a stage string alone never enables a control.
 */
export const DisputeActions: React.FC<{
  dispute: CardDispute;
  canManage: boolean;
  onUpdated: () => void;
}> = ({ dispute, canManage, onUpdated }) => {
  const responseActions = getResponseActionDescriptors(dispute);
  const closeResolutions = getCloseResolutionDescriptors(dispute);

  if (!canManage) {
    return null;
  }

  const terminal = dispute.stage === 'dispute_won' || dispute.stage === 'dispute_close';
  if (
    terminal ||
    (responseActions.length === 0 &&
      closeResolutions.length === 0 &&
      dispute.submission_status !== 'failed' &&
      !isClosurePending(dispute))
  ) {
    return null;
  }

  return (
    <div
      id="dispute-actions"
      tabIndex={-1}
      className="flex flex-col gap-4"
      data-testid="dispute-actions"
      data-dd-privacy="hidden"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-foreground">Actions</p>
        <p className="text-xs text-muted-foreground">Next step: {getNextActor(dispute)}</p>
      </div>

      {isClosurePending(dispute) && (
        <Alert data-testid="dispute-closure-pending">
          <AlertTitle>Closure/recall pending</AlertTitle>
          <AlertDescription>
            <p>
              A close ({dispute.requested_resolution ? dispute.requested_resolution.replace(/_/g, ' ') : 'requested'})
              is queued
              {dispute.requested_resolution === CardDisputeCloseResolution.ACCEPT_CARDHOLDER_CLAIM
                ? ', including the cardholder credit'
                : ''}
              . The dispute shows the terminal state once the server confirms it.
            </p>
            <p>
              Provisional credit:{' '}
              {dispute.provisional_credit ? 'issued — later reversals are not reflected here' : 'not issued'}
            </p>
          </AlertDescription>
        </Alert>
      )}

      {dispute.submission_status === 'failed' && <FailedSubmissionSection dispute={dispute} onUpdated={onUpdated} />}

      {responseActions.length > 0 && (
        <ResponseSection dispute={dispute} actions={responseActions} onUpdated={onUpdated} />
      )}

      {closeResolutions.length > 0 && (
        <CloseSection dispute={dispute} resolutions={closeResolutions} onUpdated={onUpdated} />
      )}
    </div>
  );
};

const ResponseSection: React.FC<{
  dispute: CardDispute;
  actions: DisputeResponseActionDescriptor[];
  onUpdated: () => void;
}> = ({ dispute, actions, onUpdated }) => {
  const [selection, setSelection] = useState<{ stateKey: string; descriptor: DisputeResponseActionDescriptor }>();
  const [input, setInput] = useState<RespondFormInput>(emptyRespondInput);
  const [reviewing, setReviewing] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [submissionError, setSubmissionError] = useState<string>();
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  const respondMutation = useRespondCardDispute();
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const contextKey = `${dispute.id}:${platformId ?? ''}:${isSandbox ? 'sandbox' : 'live'}`;
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;

  const addSuccessNotification = useNotificationStore((state) => state.addSuccessNotification);
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);

  const inboundEvidence = latestInboundEvidence(dispute);
  const busy = respondMutation.isPending || uploadBusy;
  const competingPending = dispute.submission_status === 'pending_submission';

  const reset = useCallback(() => {
    setSelection(undefined);
    setReviewing(false);
    setProblems([]);
    setInput(emptyRespondInput);
    setSubmissionError(undefined);
    setOutcomeUnknown(false);
  }, []);

  // The open form's copy describes the dispute state it was opened against. A refresh that moved
  // the identity, workflow, or stage (e.g. merchant_responded → prearbitration_responded turns the
  // same `escalate` action into an arbitration request) must reset the form and review — the
  // backend accepts the action in both stages and cannot catch the misleading confirmation.
  const disputeStateKey = `${dispute.id}:${dispute.workflow}:${dispute.stage}`;
  const selected = selection?.stateKey === disputeStateKey ? selection.descriptor : undefined;
  const effectiveSelected =
    selected && actions.some((action) => action.action === selected.action) ? selected : undefined;

  useEffect(() => {
    if (selection && selection.stateKey !== disputeStateKey) {
      reset();
    }
  }, [selection, disputeStateKey, reset]);

  const openForm = (descriptor: DisputeResponseActionDescriptor) => {
    setSubmissionError(undefined);
    setOutcomeUnknown(false);
    setProblems([]);
    setReviewing(false);
    setSelection({ stateKey: disputeStateKey, descriptor });
    setInput(emptyRespondInput);
  };

  const openReview = () => {
    if (!effectiveSelected) {
      return;
    }
    const found = validateRespondFormInput(dispute, effectiveSelected.action, input);
    setProblems(found);
    if (found.length === 0) {
      setReviewing(true);
    }
  };

  const sendResponse = async () => {
    if (!effectiveSelected || respondMutation.isPending) {
      return;
    }
    setSubmissionError(undefined);
    try {
      await respondMutation.mutateAsync({
        disputeId: dispute.id,
        transactionId: dispute.card_transaction_id,
        request: buildRespondRequest(dispute, effectiveSelected.action, input),
      });
      if (contextKeyRef.current !== contextKey) {
        return;
      }
      addSuccessNotification({
        content:
          'Response queued for delivery to the Card Network. It does not mean the Card Network has accepted it yet.',
      });
      reset();
      onUpdated();
    } catch (error) {
      if (contextKeyRef.current !== contextKey) {
        return;
      }
      if (isTransportFailure(error)) {
        // A transport failure has an unknown outcome: the server may have persisted the response.
        // Never optimistically show a terminal state and never auto-retry; refetch first.
        setOutcomeUnknown(true);
        onUpdated();
        return;
      }
      const message = apiErrorMessage(error, 'The response could not be sent.');
      setSubmissionError(message);
      addDangerNotification({ content: message });
      // 400/409: the problem is explained, the current actions are reloaded, and the form stays
      // available for a corrected attempt against fresh state.
      onUpdated();
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
      data-testid="dispute-response-section"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-foreground">Respond</p>
        {dispute.respond_by && (
          <p className="text-xs text-muted-foreground">Response due: {formatRespondBy(dispute.respond_by)}</p>
        )}
      </div>

      {inboundEvidence.length > 0 && (
        <div className="rounded-md bg-muted/40 p-3" data-testid="dispute-inbound-evidence">
          <p className="text-xs font-medium text-foreground">Latest received from network</p>
          {inboundEvidence.slice(0, 3).map((entry) => (
            <p key={entry.id} className="mt-1 line-clamp-2 whitespace-pre-wrap text-xs text-muted-foreground">
              {entry.narrative ?? entry.document_id ?? 'Document evidence'}
            </p>
          ))}
          <p className="mt-1 text-xs text-muted-foreground">Review the full evidence on the Evidence tab.</p>
        </div>
      )}

      {competingPending && (
        <Alert>
          <AlertTitle>Work in progress</AlertTitle>
          <AlertDescription>
            A queued submission is still being processed. Responses are disabled until it finishes.
          </AlertDescription>
        </Alert>
      )}

      {!effectiveSelected && (
        <div className="flex flex-col gap-2" data-testid="dispute-response-options">
          {actions.map((descriptor) => (
            <div key={descriptor.action} className="flex flex-col gap-1 rounded-md border border-border p-3">
              <p className="text-sm font-medium text-foreground">{descriptor.label}</p>
              <p className="text-xs text-muted-foreground">{descriptor.description}</p>
              <div>
                <Button
                  size="small"
                  variant="secondary"
                  isDisabled={busy || competingPending}
                  onClick={() => openForm(descriptor)}
                  data-testid={`dispute-response-open-${descriptor.action}`}
                >
                  {descriptor.label}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {effectiveSelected && !reviewing && (
        <div className="flex flex-col gap-3" data-testid="dispute-response-form">
          <p className="text-sm font-medium text-foreground">{effectiveSelected.label}</p>
          <p className="text-xs text-muted-foreground">{effectiveSelected.description}</p>

          {effectiveSelected.requiresAmount && (
            <div className="flex flex-col gap-1">
              <Label className="text-sm font-medium text-foreground" htmlFor="dispute-response-amount">
                Accepted liability amount ({dispute.currency})
              </Label>
              <Input
                id="dispute-response-amount"
                inputMode="decimal"
                value={input.amountInput}
                data-dd-privacy="hidden"
                data-testid="dispute-response-amount"
                onChange={(event) => setInput((current) => ({ ...current, amountInput: event.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                The portion of the {formatNumber(dispute.amount, dispute.currency)} claim the platform accepts.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <Label className="text-sm font-medium text-foreground" htmlFor="dispute-response-narrative">
              {effectiveSelected.requiresNarrative ? 'Explanation (required)' : 'Explanation (optional)'}
            </Label>
            <Textarea
              id="dispute-response-narrative"
              value={input.narrative}
              disabled={busy}
              data-dd-privacy="hidden"
              data-testid="dispute-response-narrative"
              onChange={(event) => setInput((current) => ({ ...current, narrative: event.target.value }))}
            />
          </div>

          <DisputeEvidenceComposer
            value={input.evidence}
            onChange={(evidence) => setInput((current) => ({ ...current, evidence }))}
            disabled={busy || competingPending}
            onBusyChange={setUploadBusy}
          />

          {problems.length > 0 && (
            <Alert variant="destructive" data-testid="dispute-response-problems">
              <AlertTitle>The response is not ready to send</AlertTitle>
              <AlertDescription>
                {problems.map((problem) => (
                  <p key={problem}>{problem}</p>
                ))}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="subtle" size="small" onClick={reset} isDisabled={respondMutation.isPending}>
              Cancel
            </Button>
            <Button
              size="small"
              onClick={openReview}
              isDisabled={busy || competingPending}
              data-testid="dispute-response-review"
            >
              Review and send
            </Button>
          </div>
        </div>
      )}

      {effectiveSelected && reviewing && (
        <div className="flex flex-col gap-3 rounded-md border border-border p-3" data-testid="dispute-response-review">
          <p className="text-sm font-medium text-foreground">{effectiveSelected.confirmHeadline}</p>
          <p className="text-xs text-muted-foreground">{effectiveSelected.confirmDescription}</p>
          <div className="flex flex-col gap-1 text-xs text-muted-foreground">
            <span>Dispute: {dispute.id}</span>
            {effectiveSelected.requiresAmount && (
              <span data-testid="dispute-response-review-amount">
                Accepted liability:{' '}
                {formatNumber(acceptedLiabilitySmallestUnit(dispute, input.amountInput) ?? 0, dispute.currency)}
              </span>
            )}
            {input.narrative.trim() !== '' && (
              <span className="line-clamp-3">Explanation: {input.narrative.trim()}</span>
            )}
            <span>
              Evidence:{' '}
              {input.evidence.length === 0
                ? 'none attached to the response'
                : `${input.evidence.length} document(s) sent with the response`}
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="subtle"
              size="small"
              isDisabled={respondMutation.isPending}
              onClick={() => {
                setReviewing(false);
                setProblems([]);
              }}
            >
              Back to editing
            </Button>
            <Button
              size="small"
              variant="primary"
              isLoading={respondMutation.isPending}
              isDisabled={respondMutation.isPending}
              onClick={() => void sendResponse()}
              data-testid="dispute-response-confirm"
            >
              Send response
            </Button>
          </div>
        </div>
      )}

      {outcomeUnknown && (
        <Alert variant="destructive" data-testid="dispute-response-unknown-outcome">
          <AlertTitle>Outcome unknown</AlertTitle>
          <AlertDescription>
            The connection failed before the server answered. The response may or may not have been recorded — refresh
            the dispute to see its current state before trying again.
          </AlertDescription>
        </Alert>
      )}

      {submissionError && (
        <Alert variant="destructive">
          <AlertTitle>The response was not sent</AlertTitle>
          <AlertDescription>
            {submissionError} The current actions have been reloaded — review them before trying again.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

const FailedSubmissionSection: React.FC<{ dispute: CardDispute; onUpdated: () => void }> = ({ dispute, onUpdated }) => {
  const submitMutation = useSubmitCardDispute();
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const addSuccessNotification = useNotificationStore((state) => state.addSuccessNotification);
  const intent = getSubmissionIntent(dispute);
  const retryable = canRetrySubmissionIntent(dispute);

  const retry = async () => {
    try {
      await submitMutation.mutateAsync({ disputeId: dispute.id, transactionId: dispute.card_transaction_id });
      addSuccessNotification({ content: 'Retry queued. The saved request is being submitted again.' });
      onUpdated();
    } catch (error) {
      const message = apiErrorMessage(error, 'The retry could not be queued.');
      addDangerNotification({ content: message });
      onUpdated();
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
      data-testid="dispute-failed-submission"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-foreground">Failed submission</p>
        <p className="text-xs text-muted-foreground">
          Status: {getSubmissionStatusLabel(dispute.submission_status)} · Saved request: {submissionIntentLabel(intent)}
        </p>
      </div>

      {dispute.submission_error && (
        <p
          className="break-words font-mono text-xs text-muted-foreground"
          data-testid="dispute-failed-submission-error"
        >
          {dispute.submission_error}
        </p>
      )}

      {intent.kind === 'close' && (
        <p className="text-xs text-muted-foreground">
          Requested resolution: {intent.resolution ? getCloseResolutionLabel(intent.resolution) : '—'} · Provisional
          credit: {dispute.provisional_credit ? 'issued — it is not issued again from here' : 'not issued'}
        </p>
      )}

      {retryable ? (
        <div className="flex justify-end">
          <Button
            size="small"
            variant="secondary"
            isLoading={submitMutation.isPending}
            isDisabled={submitMutation.isPending}
            onClick={() => void retry()}
            data-testid="dispute-failed-retry"
          >
            Retry saved request
          </Button>
        </div>
      ) : (
        <Alert>
          <AlertTitle>This request cannot be retried automatically</AlertTitle>
          <AlertDescription>{SUPPORT_PATH_COPY}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

const CloseSection: React.FC<{
  dispute: CardDispute;
  resolutions: DisputeCloseResolutionDescriptor[];
  onUpdated: () => void;
}> = ({ dispute, resolutions, onUpdated }) => {
  const [selection, setSelection] = useState<{ stateKey: string; descriptor: DisputeCloseResolutionDescriptor }>();
  const [narrative, setNarrative] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const closeMutation = useCloseCardDispute();
  const addSuccessNotification = useNotificationStore((state) => state.addSuccessNotification);
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);

  const busy = closeMutation.isPending;

  // Same staleness rule as the response form: closure copy is state-specific, and a queued close
  // locks its resolution server-side, so a refresh that moved the dispute resets the form.
  const disputeStateKey = `${dispute.id}:${dispute.workflow}:${dispute.stage}`;
  const selectedResolution = selection?.stateKey === disputeStateKey ? selection.descriptor : undefined;

  useEffect(() => {
    if (selection && selection.stateKey !== disputeStateKey) {
      setSelection(undefined);
      setReviewing(false);
    }
  }, [selection, disputeStateKey]);

  const close = async () => {
    if (!selectedResolution || busy) {
      return;
    }
    try {
      await closeMutation.mutateAsync({
        disputeId: dispute.id,
        transactionId: dispute.card_transaction_id,
        request: {
          resolution: selectedResolution.resolution,
          narrative: narrative.trim() !== '' ? narrative.trim() : undefined,
        },
      });
      addSuccessNotification({ content: 'Closure queued. The dispute updates when the server confirms it.' });
      setSelection(undefined);
      setNarrative('');
      setReviewing(false);
      onUpdated();
    } catch (error) {
      const message = apiErrorMessage(error, 'The dispute could not be closed.');
      addDangerNotification({ content: message });
      onUpdated();
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
      data-testid="dispute-close-section"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-foreground">Close dispute</p>
        <p className="text-xs text-muted-foreground">
          Claim: {formatNumber(dispute.amount, dispute.currency)} · Provisional credit:{' '}
          {dispute.provisional_credit ? 'issued — later reversals are not reflected here' : 'not issued'}
        </p>
      </div>

      {!selectedResolution && (
        <div className="flex flex-col gap-2" data-testid="dispute-close-options">
          {resolutions.map((descriptor) => (
            <div key={descriptor.resolution} className="flex flex-col gap-1 rounded-md border border-border p-3">
              <p className="text-sm font-medium text-foreground">{descriptor.label}</p>
              <p className="text-xs text-muted-foreground">{descriptor.description}</p>
              <div>
                <Button
                  size="small"
                  variant="secondary"
                  isDisabled={busy}
                  onClick={() => {
                    setSelection({ stateKey: disputeStateKey, descriptor });
                    setReviewing(false);
                  }}
                  data-testid={`dispute-close-open-${descriptor.resolution}`}
                >
                  {descriptor.label}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedResolution && !reviewing && (
        <div className="flex flex-col gap-3" data-testid="dispute-close-form">
          <p className="text-sm font-medium text-foreground">{selectedResolution.label}</p>
          <div className="flex flex-col gap-1">
            <Label className="text-sm font-medium text-foreground" htmlFor="dispute-close-narrative">
              Reason (optional)
            </Label>
            <Textarea
              id="dispute-close-narrative"
              value={narrative}
              disabled={busy}
              data-dd-privacy="hidden"
              data-testid="dispute-close-narrative"
              onChange={(event) => setNarrative(event.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="subtle"
              size="small"
              isDisabled={busy}
              onClick={() => {
                setSelection(undefined);
                setReviewing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={() => setReviewing(true)}
              isDisabled={busy}
              data-testid="dispute-close-review"
            >
              Review and close
            </Button>
          </div>
        </div>
      )}

      {selectedResolution && reviewing && (
        <div className="flex flex-col gap-3 rounded-md border border-border p-3" data-testid="dispute-close-review">
          <p className="text-sm font-medium text-foreground">{selectedResolution.confirmHeadline}</p>
          <p className="text-xs text-muted-foreground">{selectedResolution.confirmDescription}</p>
          <div className="flex flex-col gap-1 text-xs text-muted-foreground">
            <span>Dispute: {dispute.id}</span>
            <span data-testid="dispute-close-review-resolution">Selected resolution: {selectedResolution.label}</span>
            <span>Claim: {formatNumber(dispute.amount, dispute.currency)}</span>
            <span>
              Provisional credit:{' '}
              {dispute.provisional_credit
                ? 'issued — any reversal follows backend rules and is not shown as a live balance'
                : 'not issued'}
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="subtle"
              size="small"
              isDisabled={closeMutation.isPending}
              onClick={() => setReviewing(false)}
            >
              Back to editing
            </Button>
            <Button
              size="small"
              variant="danger"
              isLoading={closeMutation.isPending}
              isDisabled={closeMutation.isPending}
              onClick={() => void close()}
              data-testid="dispute-close-confirm"
            >
              Confirm close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
